// client/src/components/pages/LexiStar/RouteTruckDriver/EpisodesPanel.tsx
//
// The overspeed episodes behind a speeding row (ST-00256; FR-RPT-006 "every aggregate drills
// through"): GET /speeding/events for the selected route / truck / driver — or the whole period
// when no row is selected — newest first, each naming its truck, trip, zone, limit, maximum
// speed, excess and duration, and opening its trip. Driver names appear only when the API sent
// them (lexistar.drivers.view).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSpeedingEvents } from '../../../../services/lexistarAnalytics';
import type { SpeedingBy, SpeedingEpisode, SpeedingEventsResponse } from '../../../../types/lexistar-analytics';
import { formatMiningDateTime } from '../../../../utils/dateUtils';
import { SkeletonBlock, cardClasses } from '../Dashboard/analyticsAtoms';
import { fmtSeconds } from './rtdLogic';

const LIMIT = 200;

type ErrorLike = { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string };
const errorText = (err: ErrorLike): string =>
  err?.response?.status === 403
    ? 'You do not have permission to read these episodes.'
    : err?.response?.data?.error || err?.response?.data?.message || err?.message || 'The episodes could not be loaded.';

export interface EpisodesPanelProps {
  by: SpeedingBy;
  /** The selected row, or null for every episode of the period. */
  scopeId: string | null;
  label: string | null;
  from: string;
  to: string;
}

const kmh = (v: number | null) => (v === null ? '—' : `${Math.round(v)} km/h`);
const excess = (v: number | null) => (v === null ? '—' : `+${v.toLocaleString('en-GB', { maximumFractionDigits: 1 })} km/h`);

export default function EpisodesPanel({ by, scopeId, label, from, to }: EpisodesPanelProps) {
  const [data, setData] = useState<SpeedingEventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getSpeedingEvents({ by: scopeId ? by : undefined, scope_id: scopeId ?? undefined, from, to, limit: LIMIT })
      .then((res) => alive && setData(res))
      .catch((err: ErrorLike) => {
        if (!alive) return;
        setData(null);
        setError(errorText(err));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [by, scopeId, from, to]);

  const episodes: SpeedingEpisode[] = data?.episodes || [];
  const showDriver = episodes.some((e) => e.driver_label || e.driver_id);
  const title = scopeId ? `Episodes behind ${label || scopeId}` : 'Every episode in the period';

  return (
    <section className={`${cardClasses} overflow-hidden`} aria-labelledby="rtd-episodes-title" data-testid="episodes-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pt-3 pb-2">
        <h2 id="rtd-episodes-title" className="text-sm font-semibold text-slate-900">
          {title}
        </h2>
        {data && (
          <p className="text-xs text-slate-600 tabular-nums" role="status">
            {data.total} episode{data.total === 1 ? '' : 's'}
            {data.truncated ? ` · newest ${episodes.length} shown` : ''}
          </p>
        )}
      </div>
      {error ? (
        <p role="alert" className="px-4 pb-4 text-sm text-red-700">
          {error}
        </p>
      ) : loading ? (
        <div className="px-4 pb-4 space-y-2" aria-busy="true">
          {Array.from({ length: 4 }, (_, i) => (
            <SkeletonBlock key={i} className="h-6" />
          ))}
        </div>
      ) : !episodes.length ? (
        <p className="px-4 pb-6 text-sm text-slate-600">No overspeed episodes here in this period.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200 text-xs font-semibold text-slate-700 uppercase tracking-wide">
              <tr>
                <th scope="col" className="px-3 py-2 text-left whitespace-nowrap">When</th>
                <th scope="col" className="px-3 py-2 text-left">Truck</th>
                <th scope="col" className="px-3 py-2 text-left">Trip</th>
                <th scope="col" className="px-3 py-2 text-left">Route</th>
                {showDriver && <th scope="col" className="px-3 py-2 text-left">Driver</th>}
                <th scope="col" className="px-3 py-2 text-left">Zone</th>
                <th scope="col" className="px-3 py-2 text-right">Limit</th>
                <th scope="col" className="px-3 py-2 text-right">Max</th>
                <th scope="col" className="px-3 py-2 text-right">Excess</th>
                <th scope="col" className="px-3 py-2 text-right">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {episodes.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700 tabular-nums">{formatMiningDateTime(e.event_time).slice(0, 17)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-800">{e.registration || e.asset_scope}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {e.drill ? (
                      <Link to={e.drill.route} className="font-medium text-primary-700 hover:underline" title="Open the trip">
                        {e.trip_reference || 'Trip'}
                      </Link>
                    ) : (
                      <span className="text-slate-500">Not on a trip</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700 max-w-[14rem] truncate" title={e.route_label || undefined}>
                    {e.route_label || (e.trip_id ? '—' : '')}
                  </td>
                  {showDriver && <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{e.driver_label || e.driver_id || '—'}</td>}
                  <td className="px-3 py-2 text-slate-700 max-w-[12rem] truncate" title={e.zone || undefined}>
                    {e.zone || '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">{kmh(e.limit_kmh)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-800 whitespace-nowrap">{kmh(e.max_speed_kmh)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-800 whitespace-nowrap">{excess(e.excess_kmh)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtSeconds(e.duration_s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
